import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * PhotoViewer — fullscreen, pinch-zoomable image modal.
 *
 * Principles:
 *   - Click/tap the trigger image → opens fullscreen overlay with object-fit:contain
 *     so the image is never cropped badly regardless of aspect ratio.
 *   - Mobile: native pinch zoom via touch-action: pinch-zoom on the image. We don't
 *     fight the browser — the system-level gesture is superior to any hand-rolled one.
 *   - Desktop: click-and-hold drag to pan when zoomed. Wheel to zoom.
 *   - Close: ESC, backdrop click, or the × button.
 *   - Portal to <body> so the overlay escapes any ancestor transform/overflow.
 *
 * API:
 *   <PhotoViewer src="…" alt="…" className="…">  ← the trigger (card)
 *     <img src="…" className="…" />             ← OR pass custom children
 *   </PhotoViewer>
 *
 * The trigger image uses object-fit:cover by default (fills the card nicely).
 * The fullscreen image uses object-fit:contain (no cropping, respects aspect).
 */
export function PhotoViewer({
    src,
    alt = "",
    className = "",
    triggerClassName = "",
    "data-testid": testId,
}) {
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const dragRef = useRef(null);

    // ESC to close; lock body scroll while open.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open]);

    // Reset transform whenever viewer reopens.
    useEffect(() => {
        if (open) {
            setZoom(1);
            setPan({ x: 0, y: 0 });
            setLoaded(false);
        }
    }, [open]);

    const onWheel = (e) => {
        e.preventDefault();
        const delta = -e.deltaY;
        setZoom((z) => {
            const next = Math.max(1, Math.min(5, z + delta * 0.003));
            if (next === 1) setPan({ x: 0, y: 0 });
            return next;
        });
    };

    const onMouseDown = (e) => {
        if (zoom === 1) return;
        dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    };
    const onMouseMove = (e) => {
        if (!dragRef.current) return;
        setPan({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
    };
    const onMouseUp = () => { dragRef.current = null; };
    const onDoubleClick = () => {
        setZoom((z) => {
            if (z > 1) {
                setPan({ x: 0, y: 0 });
                return 1;
            }
            return 2;
        });
    };

    const overlay = open ? (
        <div
            className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-md flex items-center justify-center animate-fade-in"
            onClick={() => setOpen(false)}
            data-testid="photo-viewer-overlay"
            role="dialog"
            aria-label={alt || "photo"}
            aria-modal="true"
        >
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="absolute top-5 right-5 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors p-3 rounded-full hover:bg-white/10"
                aria-label="Close"
                data-testid="photo-viewer-close"
            >
                <X size={24} />
            </button>

            <div
                className="relative w-full h-full flex items-center justify-center pointer-events-none"
                onClick={(e) => e.stopPropagation()}
                onWheel={onWheel}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onDoubleClick={onDoubleClick}
            >
                <img
                    src={src}
                    alt={alt}
                    onLoad={() => setLoaded(true)}
                    className={`max-w-[92vw] max-h-[92vh] object-contain select-none transition-opacity duration-300 pointer-events-auto ${loaded ? "opacity-100" : "opacity-0"}`}
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: "center center",
                        // Touch-action: pinch-zoom lets the mobile browser handle pinch
                        // gestures natively — sharper, smoother, zero JS overhead.
                        touchAction: "pinch-zoom",
                        cursor: zoom > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
                        willChange: "transform",
                    }}
                    draggable={false}
                    data-testid="photo-viewer-image"
                />
            </div>

            {/* Hint: only show on first open, desktop only */}
            <p className="absolute bottom-6 inset-x-0 text-center text-white/40 text-xs tracking-wide uppercase pointer-events-none hidden md:block">
                Scroll / double-click to zoom · ESC to close
            </p>
        </div>
    ) : null;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={`relative block overflow-hidden focus:outline-none focus:ring-2 focus:ring-ink-gold/60 ${triggerClassName}`}
                data-testid={testId || "photo-viewer-trigger"}
                aria-label={alt ? `Open photo: ${alt}` : "Open photo"}
            >
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className={`w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02] ${className}`}
                    draggable={false}
                />
            </button>
            {overlay && createPortal(overlay, document.body)}
        </>
    );
}

export default PhotoViewer;
